// Shows fenced blocks in the editor as something drawn from them - a sheet's
// grid, a recipe's card - a block widget standing in for the fences and the
// text. The text stays the source: an edit in the widget is a change to the
// block's lines, so it has the editor's undo history. With the cursor inside
// a block - "Text" in the widget puts it there - it shows as plain text again.
import { type Fenced } from '@/app/fence';
import {
  redo,
  undo,
} from '@codemirror/commands';
import {
  type EditorState,
  StateField,
} from '@codemirror/state';
import {
  type Command,
  Decoration,
  type DecorationSet,
  EditorView,
  WidgetType,
} from '@codemirror/view';
import { type ReactNode } from 'react';
import {
  createRoot,
  type Root,
} from 'react-dom/client';

// what a widget can do to its block
export interface BlockActions {
  // puts this text between the fences
  replace(content: string): void;
  editText(): void;
  undo(): void;
  redo(): void;
}

export interface BlockKind<B extends Fenced> {
  name: string;
  find(body: string): B[];
  draw(block: B, on: BlockActions): ReactNode;
}

const roots = new WeakMap<HTMLElement, Root>();
const watchers = new WeakMap<HTMLElement, ResizeObserver>();

// Each widget's drawn height, by where its block starts. An edit in a block
// makes a new widget, and without a height CodeMirror counts it one line
// tall until it measures again; in between it re-anchors the scroll on that
// and jumps by the widget's height. Text moving the block just misses here.
const heights = new Map<number, number>();

// Undo and redo put back the editor's cursor from before the change, and
// scroll to it - away from the widget, where the cursor seldom is. The view's
// place, taken first, is put back after, and that scroll is the one it does
const keepScroll = (view: EditorView, command: Command) => {
  const place = view.scrollSnapshot();
  if (command(view)) view.dispatch({ effects: place });
};

class BlockWidget extends WidgetType {
  constructor(
    readonly name: string,
    readonly block: Fenced,
    // the block where it is now; text before it moves it
    readonly locate: (view: EditorView, dom: HTMLElement) => Fenced | undefined,
    readonly draw: (on: BlockActions) => ReactNode,
  ) {
    super();
  }

  override eq(other: BlockWidget) {
    return other.name === this.name
      && other.block.content === this.block.content;
  }

  override get estimatedHeight() {
    return heights.get(this.block.from) ?? -1;
  }

  toDOM(view: EditorView) {
    const dom = document.createElement('div');
    dom.className = `cm-block cm-${this.name}`;
    roots.set(dom, createRoot(dom));
    const watcher = new ResizeObserver(() => {
      const block = dom.isConnected ? this.locate(view, dom) : undefined;
      if (block) {
        heights.set(
          block.from,
          dom.getBoundingClientRect().height / view.scaleY,
        );
      }
    });
    watcher.observe(dom);
    watchers.set(dom, watcher);
    this.render(dom, view);
    return dom;
  }

  // the same widget, re-rendered, so its state (a grid's active cell)
  // survives the edit
  override updateDOM(dom: HTMLElement, view: EditorView) {
    if (!roots.has(dom)) return false;
    this.render(dom, view);
    return true;
  }

  override destroy(dom: HTMLElement) {
    const root = roots.get(dom);
    roots.delete(dom);
    watchers.get(dom)?.disconnect();
    watchers.delete(dom);
    // React won't unmount during the render CodeMirror may be inside of
    queueMicrotask(() => root?.unmount());
  }

  // the widget handles its own keys and clicks
  override ignoreEvent() {
    return true;
  }

  private render(dom: HTMLElement, view: EditorView) {
    roots.get(dom)?.render(this.draw({
      replace: content => {
        const block = this.locate(view, dom);
        if (!block) return;
        view.dispatch({
          changes: {
            from: block.contentFrom,
            to: block.contentTo,
            insert: content,
          },
          // put back where the view stood, once the widget is measured again
          effects: view.scrollSnapshot(),
          userEvent: `input.${this.name}`,
        });
      },
      editText: () => {
        const block = this.locate(view, dom);
        if (!block) return;
        view.dispatch({ selection: { anchor: block.contentFrom } });
        view.focus();
      },
      undo: () => keepScroll(view, undo),
      redo: () => keepScroll(view, redo),
    }));
  }
}

const widgetsOf = <B extends Fenced>(kind: BlockKind<B>, text: string) =>
  kind.find(text).map(block =>
    new BlockWidget(
      kind.name,
      block,
      (view, dom) => {
        const pos = view.posAtDOM(dom);
        return kind.find(view.state.doc.toString()).find(b => b.from === pos);
      },
      on => kind.draw(block, on),
    )
  );

// The first place in the text no block touches, so a cursor put there leaves
// every block drawn: the start, or the line after the blocks at the top
export const startOutsideBlocks = (kinds: BlockKind<any>[], text: string) =>
  kinds
    .flatMap(kind => kind.find(text))
    .sort((a, b) => a.from - b.from)
    .reduce(
      (pos, b) =>
        b.from <= pos && pos <= b.to ? Math.min(b.to + 1, text.length) : pos,
      0,
    );

// every kind's blocks as widgets, but the ones the selection touches
export const blocks = (kinds: BlockKind<any>[]) => {
  const build = (state: EditorState): DecorationSet => {
    const text = state.doc.toString();
    const { ranges } = state.selection;
    return Decoration.set(
      kinds
        .flatMap(kind => widgetsOf(kind, text))
        .filter(({ block: b }) =>
          !ranges.some(r => r.from <= b.to && r.to >= b.from)
        )
        .map(widget =>
          Decoration.replace({ block: true, widget })
            .range(widget.block.from, widget.block.to)
        ),
      true,
    );
  };
  return StateField.define<DecorationSet>({
    create: build,
    update: (decorations, tr) =>
      tr.docChanged || tr.selection ? build(tr.state) : decorations,
    provide: field => EditorView.decorations.from(field),
  });
};
